import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from "@auth0/auth0-react";
import { getOnboardingStatus } from "../services/backend.service";
import { callWithAuthRetry } from "../services/auth.service";
import { useSubdomain } from "../providers/SubdomainProvider";
import { usePermissions } from "../providers/PermissionsProvider";

const MAX_ONBOARDING_AUTH_RETRIES = 5;
const ONBOARDING_AUTH_RETRY_DELAY_MS = 3000;

// Define the steps and their order
const ONBOARDING_STEPS = {
  PRE_SCREEN: { path: '/pre-screen', order: 1 },
  PENDING_APPROVAL: { path: '/pending-approval', order: 2 },
  CONSENT: { path: '/consent', order: 3 },
  VERIFY: { path: '/verify', order: 4 },
  AWAIT_COUNTERSIGN: { path: '/await-countersign', order: 5 },
  PREFERENCES: { path: '/preferences', order: 6 },
  UPDATES: { path: '/updates', order: 7 },
  DONE: { path: '/thank-you', order: 8 },
  INELIGIBLE: { path: '/dashboard', order: -1 }  // Redirect ineligible users to dashboard
};

const OnboardingContext = createContext(null);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(null);
  const [availableSteps, setAvailableSteps] = useState(['PRE_SCREEN']);
  const [stepStatuses, setStepStatuses] = useState({});
  const [stepSequence, setStepSequence] = useState([]);
  const [optionalSteps, setOptionalSteps] = useState([]);
  const [fastenEnabled, setFastenEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { isPlatform } = useSubdomain();
  const { userType, loading: permissionsLoading } = usePermissions();
  const authRetryRef = useRef(0);

  const fetchOnboardingStatus = useCallback(async ({ silent = false } = {}) => {
    console.log('📋 OnboardingProvider: fetchOnboardingStatus called, isAuthenticated:', isAuthenticated, 'silent:', silent);
    if (!isAuthenticated) {
      setLoading(false);
      return null;
    }

    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      console.log('📋 OnboardingProvider: Calling getOnboardingStatus with retry...');
      const { data, error } = await callWithAuthRetry(
        getAccessTokenSilently,
        (token) => getOnboardingStatus(token)
      );
      console.log('📋 OnboardingProvider: getOnboardingStatus result:', { hasData: !!data, hasError: !!error });

      if (error) {
        const isAuthRelated = error.status === 401 || error.status === 403 ||
          (error.message && (error.message.includes('403') || error.message.includes('Forbidden')));
        if (isAuthRelated && authRetryRef.current < MAX_ONBOARDING_AUTH_RETRIES) {
          authRetryRef.current += 1;
          console.log(`⏳ Onboarding status auth error, retrying in ${ONBOARDING_AUTH_RETRY_DELAY_MS}ms (attempt ${authRetryRef.current}/${MAX_ONBOARDING_AUTH_RETRIES})...`);
          setTimeout(() => {
            fetchOnboardingStatus({ silent });
          }, ONBOARDING_AUTH_RETRY_DELAY_MS);
          return null;
        }
        setError(error);
        if (!silent) setLoading(false);
        return null;
      }
      authRetryRef.current = 0;
      if (data) {
        setCurrentStep(data.current_step);
        setAvailableSteps(data.available_steps);
        setStepStatuses(data.steps);
        setStepSequence(data.step_sequence || []);
        setOptionalSteps(data.optional_steps || []);
        setFastenEnabled(data.fasten_enabled || false);
      }
      if (!silent) setLoading(false);
      return data || null;
    } catch (err) {
      const errStatus = err.response?.status || err.status;
      const isAuthRelated = errStatus === 401 || errStatus === 403;
      if (isAuthRelated && authRetryRef.current < MAX_ONBOARDING_AUTH_RETRIES) {
        authRetryRef.current += 1;
        console.log(`⏳ Onboarding status exception (${errStatus}), retrying in ${ONBOARDING_AUTH_RETRY_DELAY_MS}ms (attempt ${authRetryRef.current}/${MAX_ONBOARDING_AUTH_RETRIES})...`);
        setTimeout(() => {
          fetchOnboardingStatus({ silent });
        }, ONBOARDING_AUTH_RETRY_DELAY_MS);
        return null;
      }
      setError(err);
      if (!silent) setLoading(false);
      return null;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    let isMounted = true;

    if (isPlatform) {
      setLoading(false);
      return;
    }

    if (permissionsLoading) {
      return;
    }

    if (userType === 'admin' || userType === 'customer') {
      setLoading(false);
      return;
    }

    authRetryRef.current = 0;
    
    const fetchData = async () => {
      if (isMounted && isAuthenticated) {
        await fetchOnboardingStatus();
      } else if (isMounted) {
        setLoading(false);
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, isPlatform, permissionsLoading, userType]);

  const canAccessStep = useCallback((step) => {
    if (!isAuthenticated) return false;
    if (loading) return false;
    return availableSteps.includes(step);
  }, [isAuthenticated, loading, availableSteps]);

  const getPathForStep = useCallback((stepKey, sequence = stepSequence) => {
    if (!stepKey) return null;
    const fromSequence = (sequence || []).find(
      (s) => s.id === stepKey || s.key === stepKey,
    );
    if (fromSequence?.path) return fromSequence.path;
    return ONBOARDING_STEPS[stepKey]?.path || null;
  }, [stepSequence]);

  const getLatestStep = useCallback(() => {
    if (!isAuthenticated) return '/';
    if (!currentStep) return ONBOARDING_STEPS.PRE_SCREEN.path;
    return getPathForStep(currentStep) || '/dashboard';
  }, [isAuthenticated, currentStep, getPathForStep]);

  const navigateToLatestStep = useCallback((statusOverride = null) => {
    if (statusOverride?.current_step) {
      const path = getPathForStep(
        statusOverride.current_step,
        statusOverride.step_sequence || stepSequence,
      );
      if (path) {
        navigate(path);
        return;
      }
    }
    navigate(getLatestStep());
  }, [getPathForStep, stepSequence, navigate, getLatestStep]);

  const completeStep = useCallback(async (step) => {
    console.log("completing step: ", step);
    const data = await fetchOnboardingStatus({ silent: true });
    if (data?.current_step) {
      navigateToLatestStep(data);
      return;
    }
    navigateToLatestStep();
  }, [fetchOnboardingStatus, navigateToLatestStep]);

  return (
    <OnboardingContext.Provider
      value={{
        currentStep,
        availableSteps,
        stepStatuses,
        stepSequence,  // New: server-driven step sequence
        optionalSteps,
        fastenEnabled,
        loading,
        error,
        canAccessStep,
        getLatestStep,
        navigateToLatestStep,
        completeStep,
        fetchOnboardingStatus,
        ONBOARDING_STEPS
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}; 